import { FormikErrors } from 'formik';

import { SupplierGroupSettingsMessage } from '../../../../../../types/SupplierGroupSettingsMessage';
import { UserRole } from '../../../../../../types/UserRole';
import { Button } from '../../../../../cdl/Button/Button';
import { Modal } from '../../../../../cdl/Modal/Modal';
import { Text } from '../../../../../cdl/Text/Text';
import { useToasts } from '../../../../../cdl/Toast/useToasts';
import { identifyUserRole } from '../../../../../common/helpers/identifyUserRole.helper';
import { translate } from '../../../../../common/helpers/translate.helper';
import { Box } from '../../../../../common/ui/Box';
import { companyAccessLevel, groupAccessLevel } from '../../../../../user/list/utils/UserAccessLevels';
import { SsoGroupForm, SsoGroupFormValues } from '../../../../components/SsoGroupForm';
import { useSupplierGroupSettings } from '../../../../customer/detail/hooks/useSupplierGroupSettings';
import { useSupplierGroupSettingsUpdate } from '../../../../customer/detail/hooks/useSupplierGroupSettingsUpdate';
import { SsoGroup } from '../../../../utils/transformGroupMapping';

interface SupplierEditSsoGroupModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    groupId: string;
    group: SsoGroup;
}

export const SupplierEditSsoGroupModal = ({ isOpen, onDismiss, groupId, group }: SupplierEditSsoGroupModalProps) => {
    const settingsQuery = useSupplierGroupSettings(groupId);
    const updateMutation = useSupplierGroupSettingsUpdate(groupId);
    const { addToast, addErrorToast } = useToasts();

    const initialValues: SsoGroupFormValues = {
        groupName: group.groupName,
        role: identifyUserRole(group.accessLevel, group.companyAccesses),
        companyIds: group.companyAccesses?.map((ca) => ca.companyId) || [],
    };

    const validate = (values: SsoGroupFormValues): FormikErrors<SsoGroupFormValues> => {
        const errors: FormikErrors<SsoGroupFormValues> = {};

        if (!values.groupName || values.groupName.trim().length === 0) {
            errors.groupName = translate('sso.groupNameRequired');
        } else if (!/^[a-zA-Z0-9\s_-]+$/.test(values.groupName.trim())) {
            errors.groupName = translate('sso.groupNameInvalidCharacters');
        } else if (values.groupName.trim() !== group.groupName) {
            if (settingsQuery.data?.ssoConfiguration?.samlGroupMapping[values.groupName.trim()]) {
                errors.groupName = translate('sso.groupNameDuplicate');
            }
        }

        if (!values.role) {
            errors.role = translate('sso.roleRequired');
        }

        if (values.role !== UserRole.GROUP_ADMIN && !values.companyIds?.length) {
            errors.companyIds = translate('user.error.requiredCompanyIds');
        }

        return errors;
    };

    const handleSubmit = async (values: SsoGroupFormValues) => {
        if (!settingsQuery.data?.ssoConfiguration || !values.role) return;

        const trimmedGroupName = values.groupName.trim();
        const newGroup = {
            accessLevel: groupAccessLevel[values.role],
            companyAccesses:
                values.role === UserRole.GROUP_ADMIN
                    ? undefined
                    : values.companyIds.map((companyId) => ({
                          companyId,
                          accessLevel: companyAccessLevel[values.role!],
                      })),
        };

        const newSamlGroupMapping = { ...settingsQuery.data.ssoConfiguration.samlGroupMapping };

        if (trimmedGroupName !== group.groupName) {
            delete newSamlGroupMapping[group.groupName];
        }

        newSamlGroupMapping[trimmedGroupName] = newGroup;

        const updatedSettings: SupplierGroupSettingsMessage = {
            ...settingsQuery.data,
            ssoConfiguration: {
                ...settingsQuery.data.ssoConfiguration,
                samlGroupMapping: newSamlGroupMapping,
            },
        };

        try {
            await updateMutation.mutateAsync({
                groupId,
                settings: updatedSettings,
            });
            addToast(translate('sso.updateGroupSuccess', { groupName: trimmedGroupName }));
            onDismiss();
        } catch {
            addErrorToast(translate('sso.updateGroupError'));
        }
    };

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss}>
            <Box display="flex" flexDirection="column" gap={24} padding={24}>
                <Box display="flex" flexDirection="column" gap={8}>
                    <Text variant="subtitle1" as="h2">
                        {translate('sso.editGroupModal.title')}
                    </Text>
                </Box>

                <SsoGroupForm
                    groupId={groupId}
                    groupType="supplier"
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validate={validate}
                    enableReinitialize
                >
                    {({ isValid, dirty }) => (
                        <Box display="flex" justifyContent="flex-end" gap={8}>
                            <Button type="button" emphasis="medium" onClick={onDismiss}>
                                {translate('btn.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                emphasis="high"
                                disabled={!isValid || !dirty}
                                isLoading={updateMutation.isPending}
                            >
                                {translate('sso.saveGroupButton')}
                            </Button>
                        </Box>
                    )}
                </SsoGroupForm>
            </Box>
        </Modal>
    );
};
